import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import PricingModal from '../components/PricingModal';
import { billingService } from '../services/billing';
import { LogoText } from '../components/ui/Logo';
import { useBusinessAnalytics } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { extractErrorMessage } from '../utils/errorUtils';
import { parseSafeAppReturnPath } from '../utils/billingNavigation';

export default function BillingUpgradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { trackUpgradeClick, trackFeatureUsed } = useBusinessAnalytics();
  const { isAuthenticated, isLoading, user, session } = useAuth();
  const [showPricingModal, setShowPricingModal] = useState(true);

  const returnParam = searchParams.get('return');
  const upgradeSourceParam = searchParams.get('source');
  const safeReturn = useMemo(() => parseSafeAppReturnPath(returnParam), [returnParam]);
  const afterDismissPath = safeReturn ?? '/dashboard';

  // Check authentication status
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log('🚫 BillingUpgradePage: User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
    
    if (!isLoading && isAuthenticated) {
      console.log('✅ BillingUpgradePage: User authenticated', {
        userId: user?.id,
        email: user?.email,
        hasSession: !!session,
        hasAccessToken: !!session?.access_token
      });
    }
  }, [isLoading, isAuthenticated, user, session, navigate]);

  // Track page view
  useEffect(() => {
    if (isAuthenticated) {
      trackFeatureUsed('billing_upgrade_page_view', {
        source: upgradeSourceParam ?? 'billing_page',
        timestamp: new Date().toISOString()
      });
    }
  }, [trackFeatureUsed, isAuthenticated, upgradeSourceParam]);

  const handleSelectPlan = async (planId: string, billingCycle: 'monthly' | 'annual' = 'monthly', couponCode?: string) => {
    try {
      if (planId === 'free') {
        // Free plan doesn't require billing
        navigate(afterDismissPath);
      } else if (planId !== 'free') {
        await billingService.upgradeTier(planId, billingCycle, couponCode);
      }
      setShowPricingModal(false);
    } catch (error) {
      console.error('Error upgrading plan:', error);
      toast.error(t('billing.checkoutErrorTitle', { ns: 'pricing' }), {
        description: extractErrorMessage(
          error,
          t('billing.checkoutErrorDescription', { ns: 'pricing' })
        ),
      });
    }
  };

  const handleClose = () => {
    setShowPricingModal(false);
    navigate(afterDismissPath);
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex justify-between h-16 items-center">
            <LogoText 
              variant="dark" 
              size="md" 
              iconSize={32}
              className="text-foreground"
            />
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(afterDismissPath)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="text-center space-y-6 mb-8">
          <div className="flex justify-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Crown className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground">
            Upgrade Your Memory Workspace
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Unlock unlimited memories, advanced features, and premium support to supercharge your AI's memory system.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          <div className="text-center p-6 bg-white rounded-lg border shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">♾️</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Unlimited Memories</h3>
            <p className="text-muted-foreground text-sm">Store as many memories as you need without worrying about limits.</p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-lg border shadow-sm">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🚀</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">50K API Requests</h3>
            <p className="text-muted-foreground text-sm">High-volume API access for your most demanding applications.</p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-lg border shadow-sm">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💎</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Premium Support</h3>
            <p className="text-muted-foreground text-sm">Priority support with faster response times and dedicated help.</p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button 
            onClick={() => {
              // Track the main CTA button click
              trackUpgradeClick({
                source: 'billing_page',
                current_tier: 'free', // Assuming most visitors are free tier
                target_tier: 'pro',
                upgrade_context: 'general',
                element_type: 'button',
                trigger_reason: 'main_cta_click'
              });
              
              setShowPricingModal(true);
            }}
            size="lg"
            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
          >
            <Crown className="w-5 h-5 mr-2" />
            View Pricing Plans
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            30-day money-back guarantee • Cancel anytime
          </p>
        </div>
      </main>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={handleClose}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
}
